import React, { ReactElement } from 'react';
import { cartState } from 'app-state/cart-state';
import useCartActions from 'app-state/cart-actions';
import { Button } from 'components/common/Button';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { Product } from 'utils/Product';
import CartContentsMessage from 'components/common/CartContentsMessage';
import { useRecoilValue } from 'recoil';
import CartTable from 'components/common/CartTable';

export const EmptyCartPage = (): ReactElement => (
  <div className='py-8'>
    <div className='text-2xl pb-8'>Your cart is empty.</div>
    <div><Button primary id='start_shopping_button' linkTo='/shop'>
      Start Shopping <span className='text-lg'>&#8594;</span>
    </Button></div>
  </div>
);

const FilledCartPage = (): ReactElement => {
  const { emptyCart, removeCartItem } = useCartActions();
  const [emptyCartModalOpen, setEmptyCartModalOpen] = React.useState(false);
  const [removingItem, setRemovingItem] = React.useState(null as Product | null);

  return <React.Fragment>
    <div className='w-auto p-4 md:w-4/5 xl:w-3/5 2xl:w-1/2 m-auto'>
      <div className="py-8 grid grid-cols-1 gap-2 place-items-center">
        <CartContentsMessage includeTotal />

        <CartTable
          onRemoveItem={(item) => setRemovingItem(item.product)}
          showTotal
          borderBottom
          quantityModifierStyle='buttons' />

        <div className='flex flex-col md:flex-row w-full'>
          <span className='flex justify-center md:flex-start mb-2 md:m-auto'>
            <Button grey id='continue-shopping-button' linkTo='/shop' className='w-40'>Continue Shopping</Button>
          </span>
          <span className='hidden md:block flex flex-grow' />
          <span className='flex items-center md:flex-end flex-col md:flex-row' >
            <span className='md:pr-3'>
              <Button
                id='empty-cart-button'
                danger
                className='w-40 mb-2 md:m-auto'
                onClick={() => setEmptyCartModalOpen(true)}>Empty Cart</Button>
            </span>
            <Button primary id='checkout-button' linkTo='/checkout' className='w-40'>Checkout</Button>
          </span>
        </div>
      </div>
    </div>

    <ConfirmationModal
      id='empty-cart-modal'
      isOpen={emptyCartModalOpen}
      heading='Empty Cart'
      onConfirm={() => { emptyCart(); setEmptyCartModalOpen(false); }}
      onCancel={() => setEmptyCartModalOpen(false)}
    >
      Are you sure you want to empty your cart?
    </ConfirmationModal>
    <ConfirmationModal
      id='remove-item-modal'
      isOpen={removingItem !== null}
      heading={`Remove "${removingItem?.title}" from cart?`}
      onConfirm={() => { removingItem && removeCartItem(removingItem.id); setRemovingItem(null); }}
      onCancel={() => setRemovingItem(null)}
    >
      Are you sure you want to remove this item from your cart?
    </ConfirmationModal>
  </React.Fragment>;
};

const CartPage = (): ReactElement => {
  const { cartUniqueItemCount } = useRecoilValue(cartState);
  return (cartUniqueItemCount === 0 ? <EmptyCartPage /> : <FilledCartPage />);
};

export default CartPage;
